import {
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch
} from "react-hook-form";
import React, { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import Input, { InputProps } from "@/components/ui/input";
import { fromPercentInput } from "@/utils/numbers";
import DateTimeField, {
  DateTimeFieldProps
} from "@/components/controls/DateTimeField";
import { cn } from "@/utils/utils";
import {
  MultiSelect,
  MultiSelectProps
} from "@/components/controls/MultiSelect";
import TextareaAutosize, {
  TextareaAutosizeProps
} from "react-textarea-autosize";
import SingleSelect, {
  SingleSelectProps
} from "@/components/controls/SingleSelect";
import RemindersField, {
  RemindersFieldProps
} from "@/components/controls/RemindersField/RemindersField";
import AttachmentsField, {
  AttachmentsFieldProps
} from "@/components/controls/AttachmentsField";
import { getFieldValidation } from "@/components/controls/Form/helpers";
import { useNewFieldsUI } from "@/services/features/useNewFieldsUI";
import {
  parseFileFieldValue,
  serializeFileFieldValue
} from "@/utils/entityFieldsUtils/fileFieldUtils";
import {
  FormFieldNewType,
  TextEditorProps,
  DateRangeFieldProps,
  RadioGroupProps
} from "@/components/controls/Form/types";
import MultiEmailField, {
  MultiEmailFieldProps
} from "@/components/controls/MultiEmailField";
import SingleCheckbox, {
  SingleCheckboxProps
} from "@/components/ui/SingleCheckbox";
import Checkmark from "@/components/ui/checkmark";
import TextEditor from "@/components/controls/TextEditor/TextEditor";
import DateRangeField from "@/components/controls/DateRangeField";
import {
  RadioGroup,
  RadioGroupItem,
  RadioOption
} from "@/components/ui/radio-group";
import Tabs, { tabsPropsType } from "@/components/controls/Tabs";
import Text from "@/components/ui/text";
import EntitySelectField from "@/components/controls/EntitySelectField";
import EntityMultiSelectField from "@/components/controls/EntityMultiSelectField";
import CurrencySelectField from "@/components/controls/CurrencySelectField";
import { PhoneControl } from "@/utils/fieldRendering/controls/PhoneControl";
import { decodeCheckbox } from "@/utils/fieldRendering/legacyBoolTokens";
import { FieldDisplayType } from "@/pages/settings/components/dealsSettings/FieldManagement/types";
import {
  DATE_FIELD_BADGE_CLASSNAME,
  FIELD_CLASSNAME,
  FOCUS_CLASSNAME
} from "@/utils/fieldRendering/shared";

const INVALID_FIELD_BORDER_STYLE =
  "border-[0.5px] border-border-field-error hover:border-border-field-error-hover focus:border-border-field-error-focus focus:bg-surface-focus-selected focus:shadow-field-focus";

export function useFieldItem() {
  const { t } = useTranslation();
  const isNewFieldsUI = useNewFieldsUI();

  const findDependentFields = (
    changedFieldName: string,
    allFields: FormFieldNewType[]
  ): string[] => {
    return allFields
      .filter((field) => field.dependencies?.includes(changedFieldName))
      .map((field) => field.name);
  };

  const renderField = (
    field: FormFieldNewType,
    setValue: UseFormSetValue<Record<string, any>>,
    watch: UseFormWatch<Record<string, any>>,
    hasError: boolean,
    register: UseFormRegister<Record<string, any>>,
    allFields?: FormFieldNewType[]
  ): ReactNode => {
    const setFormFieldValue = (field: string, value: any) => {
      setValue(field, value, { shouldValidate: true });
    };

    const clearDependentFields = (changedFieldName: string) => {
      if (allFields) {
        const dependentFields = findDependentFields(
          changedFieldName,
          allFields
        );
        dependentFields.forEach((dependentField) => {
          setValue(dependentField, "");
        });
      }
    };

    // Safely get field value
    const getFieldValue = (fieldName: string) => {
      try {
        // Ensure watch is a function before calling it
        if (typeof watch === "function") {
          return watch(fieldName);
        } else {
          console.error("Watch is not a function in renderField");
          return undefined;
        }
      } catch (error) {
        console.error(`Error watching field ${fieldName}:`, error);
        return undefined;
      }
    };

    const fieldValue = getFieldValue(field.name);

    switch (field.type) {
      case "singleSelect":
        const [isOpen, setIsOpen] = useState(false);
        const singleSelectProps =
          field.fieldActionProps as SingleSelectProps & {
            isEntityField?: boolean;
            displayType?: string;
            onCreateNew?: () => void;
            createNewLabel?: string;
            config?: any;
            customOnChange?: (value: string) => void;
            allowUsersToAdd?: boolean;
            onCreateNewOption?: (optionName: string) => Promise<void>;
            popOverFooter?: ReactNode;
            showFixedOptions?: boolean;
            excludeIds?: string[];
            showPlaceholderIcon?: boolean;
          };
        const processedValue =
          fieldValue === "" &&
          singleSelectProps.treatNullAsValue &&
          field.defaultValue === null
            ? null
            : fieldValue;
        const { ref, ...registerProps } = register(
          field.name,
          getFieldValidation(field, watch)
        );

        let modifiedPopOverFooter:
          | JSX.Element
          | ((searchTerm: string) => JSX.Element)
          | undefined = singleSelectProps.popOverFooter;
        if (
          singleSelectProps.popOverFooter &&
          singleSelectProps.allowUsersToAdd
        ) {
          if (typeof singleSelectProps.popOverFooter === "function") {
            const originalFooterFunction = singleSelectProps.popOverFooter;
            modifiedPopOverFooter = (searchTerm: string) => {
              const footerElement = originalFooterFunction(searchTerm);
              return React.cloneElement(footerElement, {
                onClick: (e: React.MouseEvent) => {
                  setIsOpen(false);
                  if (footerElement.props.onClick) {
                    footerElement.props.onClick(e);
                  }
                }
              });
            };
          } else {
            const originalFooter =
              singleSelectProps.popOverFooter as React.ReactElement;
            modifiedPopOverFooter = React.cloneElement(originalFooter, {
              onClick: (e: React.MouseEvent) => {
                setIsOpen(false);
                if (originalFooter.props.onClick) {
                  originalFooter.props.onClick(e);
                }
              }
            });
          }
        }

        if (singleSelectProps.isEntityField && singleSelectProps.displayType) {
          const dependentValues: Record<string, string | null> = {};

          // For PEOPLE fields, get the companyId value
          if (singleSelectProps.displayType === "people") {
            const companyIdValue = getFieldValue("companyId");
            if (companyIdValue) {
              dependentValues.companyId = companyIdValue.toString();
            }
          }

          // For LICENSES fields, get the productId value
          if (singleSelectProps.displayType === "licenses") {
            const productIdValue = getFieldValue("productId");
            if (productIdValue) {
              dependentValues.productId = productIdValue.toString();
            }
          }

          // For PARTNER_CONTACTS fields, filter by the selected partner
          if (
            singleSelectProps.displayType === FieldDisplayType.PARTNER_CONTACTS
          ) {
            const partnerFieldName = allFields?.find(
              (f) =>
                (f.fieldActionProps as { displayType?: string })
                  ?.displayType === FieldDisplayType.PARTNERS
            )?.name;
            const partnerValue = partnerFieldName
              ? getFieldValue(partnerFieldName)
              : undefined;
            if (partnerValue) {
              dependentValues.partner = partnerValue.toString();
            }
          }

          return (
            <EntitySelectField
              fieldName={field.name}
              displayType={singleSelectProps.displayType}
              value={processedValue?.toString() || null}
              onChange={(value) => {
                setFormFieldValue(field.name, value);
                clearDependentFields(field.name);
                if (value && singleSelectProps.customOnChange) {
                  singleSelectProps.customOnChange(value);
                }
              }}
              placeholder={singleSelectProps.placeholder}
              required={field.required}
              disabled={false}
              dependentValues={dependentValues}
              excludeIds={singleSelectProps.excludeIds}
              onCreateNew={singleSelectProps.onCreateNew}
              createNewLabel={singleSelectProps.createNewLabel}
              config={singleSelectProps.config}
              // A form field that names no variant gets the form body, not
              // EntitySelectField's inline default — the same fallback the
              // multi-select branch below already applies.
              variant={singleSelectProps.variant ?? "form"}
              showFixedOptions={singleSelectProps.showFixedOptions}
              customFixedOptions={singleSelectProps.fixedOptions}
              hasError={hasError}
            />
          );
        }
        return (
          <SingleSelect
            {...singleSelectProps}
            {...registerProps}
            fieldName={field.name}
            options={singleSelectProps.options}
            selectedValue={processedValue}
            clearable={
              singleSelectProps.clearable !== undefined
                ? singleSelectProps.clearable
                : !field.required
            }
            onSelection={(value) => {
              setIsOpen(false);
              setFormFieldValue(field.name, value);
              clearDependentFields(field.name);
              singleSelectProps.onSelection?.(
                value !== null ? value.toString() : value
              );
            }}
            onClear={
              !field.required
                ? () => {
                    setFormFieldValue(field.name, "");
                    clearDependentFields(field.name);
                    singleSelectProps.onSelection?.("");
                  }
                : undefined
            }
            isOpen={isOpen}
            onOpenChange={setIsOpen}
            popOverFooter={modifiedPopOverFooter}
            variant={singleSelectProps.variant === "chip" ? "chip" : "form"}
            className={cn("pl-0", field?.fieldClassName)}
            hasError={hasError}
            showPlaceholderIcon={singleSelectProps?.showPlaceholderIcon}
            disabled={field?.disabled ?? singleSelectProps?.disabled}
          />
        );

      case "multiSelect": {
        const multiSelectProps = field.fieldActionProps as MultiSelectProps & {
          isEntityField?: boolean;
          displayType?: string;
          onCreateNew?: () => void;
          createNewLabel?: string;
          config?: { placeholder?: string };
          startEmpty?: boolean;
        };

        if (multiSelectProps.isEntityField && multiSelectProps.displayType) {
          const dependentValues: Record<string, string | null> = {};
          if (
            multiSelectProps.displayType === FieldDisplayType.PARTNER_CONTACTS
          ) {
            const partnerValue = getFieldValue("partner");
            if (partnerValue) {
              dependentValues.partner = partnerValue.toString();
            }
          }
          return (
            <EntityMultiSelectField
              fieldName={field.name}
              displayType={multiSelectProps.displayType}
              value={Array.isArray(fieldValue) ? fieldValue : []}
              onChange={(newValues) => {
                setFormFieldValue(field.name, newValues);
              }}
              placeholder={multiSelectProps.placeholder}
              dependentValues={dependentValues}
              onCreateNew={multiSelectProps.onCreateNew}
              createNewLabel={multiSelectProps.createNewLabel}
              config={multiSelectProps.config}
              startEmpty={multiSelectProps.startEmpty}
              className={cn("w-full", hasError && INVALID_FIELD_BORDER_STYLE)}
            />
          );
        }

        return (
          <MultiSelect
            {...multiSelectProps}
            selectedValues={fieldValue || []}
            options={multiSelectProps.options}
            onValueChange={(newValues: string[]) => {
              setFormFieldValue(field.name, newValues);
              if (multiSelectProps.onValueChange) {
                multiSelectProps.onValueChange(newValues);
              }
            }}
            showIconInChip
            maxCount={multiSelectProps.maxCount ?? 5}
            matchTriggerWidth
            className={cn("w-full", hasError && INVALID_FIELD_BORDER_STYLE)}
            placeholderClassName="text-tertiary"
            activeClassName="!border-primary"
          />
        );
      }

      case "phone":
        // Registered for its validation rule only, like attachments below:
        // the control owns its value and reports through onChange.
        register(field.name, getFieldValidation(field, watch));
        return (
          <PhoneControl
            fieldType={FieldDisplayType.PHONE}
            name={field.name}
            value={fieldValue}
            onChange={(value) => setFormFieldValue(field.name, value)}
            wrapperClassName="w-full"
            commitOnChange
          />
        );

      case "email":
      case "text":
      case "number": {
        const inputProps = field?.fieldActionProps as InputProps;
        const baseValidation = getFieldValidation(field, watch);
        const registerOptions = field.isPercentage
          ? {
              ...baseValidation,
              setValueAs: (raw: string) => {
                if (raw === "" || raw === null || raw === undefined)
                  return undefined;
                const stored = fromPercentInput(raw);
                return stored === null ? raw : stored;
              }
            }
          : baseValidation;
        return (
          <Input
            {...register(field.name, registerOptions)}
            data-test={`${field.name}-input`}
            type={field.type}
            className={cn("w-full", hasError && INVALID_FIELD_BORDER_STYLE)}
            onBlur={(e) => {
              register(field.name).onBlur(e);
              field.onBlur?.(e.target.value);
            }}
            {...inputProps}
          />
        );
      }
      case "time":
      case "date":
        const [isDateTimeOpen, setIsDateTimeOpen] = useState(false);
        const datePickerProps = field.fieldActionProps as DateTimeFieldProps;
        return (
          <DateTimeField
            {...datePickerProps}
            {...register(field.name, getFieldValidation(field, watch))}
            type={field.type}
            value={fieldValue}
            // Caller-supplied placeholders pass through either way; only the
            // label-derived fallback is part of the redesign, and base showed
            // an empty date field with no prompt at all.
            placeholder={
              datePickerProps.placeholder ||
              (isNewFieldsUI && field.label
                ? t("common.selectFieldPlaceholder", { field: field.label })
                : undefined)
            }
            onChange={(value) => {
              setFormFieldValue(field.name, value);
              // Call the original onChange from fieldActionProps if it exists
              if (datePickerProps.onChange) {
                datePickerProps.onChange(value);
              }
            }}
            name={field.name}
            // Same reason as Form.tsx: DateTimeField owns its clear control.
            required={field.required}
            variant={datePickerProps.variant ?? "form"}
            isOpen={isDateTimeOpen}
            onOpenChange={setIsDateTimeOpen}
            className={cn(
              datePickerProps.variant === "badge"
                ? DATE_FIELD_BADGE_CLASSNAME
                : FIELD_CLASSNAME,
              isDateTimeOpen && FOCUS_CLASSNAME,
              hasError && INVALID_FIELD_BORDER_STYLE,
              datePickerProps.className
            )}
          />
        );
      case "reminders":
        const reminderProps = field.fieldActionProps as RemindersFieldProps;
        return (
          <RemindersField
            {...reminderProps}
            {...register(field.name, getFieldValidation(field, watch))}
            value={fieldValue}
            onChange={(value) => {
              setFormFieldValue(field.name, value);
            }}
            onUpdateOnlyNotifyAssignee={(value) => {
              setFormFieldValue("shouldOnlyNotifyAssignee", value);
            }}
            name={field.name}
          />
        );
      case "attachments":
        const attachmentsProps =
          field.fieldActionProps as AttachmentsFieldProps;
        // Registered for its validation rule only. The props register() returns
        // drive a native input's value and ref, and spreading them onto this
        // composite would fight the value/onChange pair below — but without the
        // call the rule is never attached and a required file field submits
        // empty.
        register(field.name, getFieldValidation(field, watch));
        return (
          <AttachmentsField
            // The other form renderer opts into the same thing: both are the
            // design's edit mode, where the field owns a block of its own and
            // opens on the Large drag-and-drop area, not the row's button. It
            // sits ahead of the spread so a field can still configure its own.
            variant="edit"
            {...attachmentsProps}
            value={
              field.isSerializedFileField
                ? parseFileFieldValue(fieldValue)
                : fieldValue
            }
            onChange={(value) => {
              setFormFieldValue(
                field.name,
                field.isSerializedFileField
                  ? serializeFileFieldValue(value || [])
                  : value
              );
            }}
            name={field.name}
          />
        );
      case "multiEmail":
        const multiEmailProps = field.fieldActionProps as MultiEmailFieldProps;
        return (
          <MultiEmailField
            {...multiEmailProps}
            {...register(field.name, getFieldValidation(field, watch))}
            value={fieldValue}
            onChange={(value) => {
              setFormFieldValue(field.name, value);
            }}
            onValidationChange={(hasErrors) => {
              field.onValidationChange?.(field.name, hasErrors);
            }}
            name={field.name}
          />
        );
      case "textarea":
        const textareaProps = field.fieldActionProps as TextareaAutosizeProps;
        return (
          <TextareaAutosize
            {...textareaProps}
            {...register(field.name, getFieldValidation(field, watch))}
            value={fieldValue}
            minRows={textareaProps.minRows || 3}
            maxRows={textareaProps.maxRows || 6}
            onBlur={(e) => {
              register(field.name).onBlur(e);
              field.onBlur?.(e.target.value);
            }}
            className={cn(
              "w-full bg-surface-field-default border-[0.5px] border-border-field-default rounded-lg px-2 py-[6px] text-base font-normal leading-[18px] outline-none placeholder:text-tertiary hover:border-border-field-hover focus:border-border-field-focus focus:bg-surface-focus-selected focus:shadow-field-focus shadow-field",
              hasError &&
                "!border-border-field-error hover:!border-border-field-error-hover focus:!border-border-field-error-focus focus:!bg-surface-focus-selected focus:!shadow-field-focus",
              textareaProps.disabled &&
                "opacity-50 cursor-not-allowed !shadow-none",
              textareaProps.className
            )}
          />
        );
      case "checkbox":
        const checkboxProps = field.fieldActionProps as SingleCheckboxProps;

        return (
          <div className="flex items-center">
            <SingleCheckbox
              {...checkboxProps}
              id={`checkbox-${field.name}`}
              isChecked={
                checkboxProps.isChecked !== undefined
                  ? checkboxProps.isChecked
                  : decodeCheckbox(fieldValue)
              }
              onCheckedChange={(checked) => {
                if (checkboxProps.onCheckedChange) {
                  checkboxProps.onCheckedChange(checked);
                } else {
                  setFormFieldValue(field.name, checked as boolean);
                }
              }}
            />
          </div>
        );

      case "checklistItem":
        return (
          <div className="flex items-center">
            <Checkmark
              id={`checklist-item-${field.name}`}
              label={
                decodeCheckbox(fieldValue) ? t("completed") : t("notCompleted")
              }
              checked={decodeCheckbox(fieldValue)}
              onCheckedChange={(checked) =>
                setFormFieldValue(field.name, checked)
              }
            />
          </div>
        );

      case "textEditor":
        const textEditorProps = field.fieldActionProps as TextEditorProps;
        return (
          <TextEditor
            {...textEditorProps}
            value={fieldValue}
            onBlur={(value) => {
              setFormFieldValue(field.name, value);
              field.onBlur?.(value);
            }}
            variant="form"
          />
        );

      case "dateRange":
        const dateRangeProps = field.fieldActionProps as DateRangeFieldProps;
        return (
          <DateRangeField
            {...dateRangeProps}
            value={fieldValue}
            onUpdate={(value: { fromDate: string; toDate: string }) => {
              setFormFieldValue(field.name, value);
            }}
            fieldKey={field.name}
            className={cn(
              hasError ? INVALID_FIELD_BORDER_STYLE : "border-border-border2"
            )}
            // Gated for the same reason as the datePicker case above.
            placeholder={
              dateRangeProps.placeholder ||
              (isNewFieldsUI && field.label
                ? t("common.selectFieldPlaceholder", { field: field.label })
                : undefined)
            }
            // Withheld for a required field, like DetailFields' sibling
            // dateRange case — formFields threads `required` straight from the
            // custom-field API, so a required range was clearable here.
            onClear={
              !field.required
                ? () => setFormFieldValue(field.name, null)
                : undefined
            }
            disabled={dateRangeProps.disabled}
          />
        );

      case "radio":
        const radioProps = field.fieldActionProps as RadioGroupProps;
        return (
          <RadioGroup
            {...register(field.name, getFieldValidation(field, watch))}
            value={fieldValue}
            onValueChange={(value) => {
              setFormFieldValue(field.name, value);
            }}
            defaultValue={(field?.defaultValue as string) || ""}
            className={cn("gap-0", hasError ? "text-red-500" : "")}
            disabled={radioProps?.disabled}
          >
            {(radioProps.options as RadioOption[])?.map((option) => (
              <div key={option.value} className="flex items-center space-x-2">
                <div className="size-8 flex items-center justify-center">
                  <RadioGroupItem
                    value={option.value}
                    id={`${field.name}-${option.value}`}
                  />
                </div>
                <div>
                  {option.label && (
                    <label htmlFor={`${field.name}-${option.value}`}>
                      <Text variant="body">{option.label}</Text>
                    </label>
                  )}
                  {option.description && (
                    <Text variant="bodySecondary">{option.description}</Text>
                  )}
                </div>
              </div>
            ))}
          </RadioGroup>
        );

      case "switch":
        const switchProps = field.fieldActionProps as tabsPropsType;
        return (
          <div className="flex items-center">
            <Tabs
              {...switchProps}
              value={fieldValue}
              valueSelected={(value) => {
                setFormFieldValue(field.name, value);
              }}
              defaultValue={(field?.defaultValue as string) || ""}
              size="lg"
            />
          </div>
        );

      case "tabs":
        const tabsProps = field.fieldActionProps as tabsPropsType;
        return (
          <div className="flex items-center w-full">
            <Tabs
              {...tabsProps}
              value={fieldValue}
              valueSelected={(value) => {
                setFormFieldValue(field.name, value);
              }}
              defaultValue={(field?.defaultValue as string) || ""}
              size="lg"
            />
          </div>
        );

      case "currencySelect":
        const currencySelectProps = field.fieldActionProps as {
          placeholder?: string;
        };
        return (
          <CurrencySelectField
            fieldName={field.name}
            value={fieldValue || null}
            onChange={(value) => {
              setFormFieldValue(field.name, value);
            }}
            placeholder={currencySelectProps?.placeholder}
            variant="form"
          />
        );

      case "render":
        return <>{field.renderNode}</>;
      default:
        return null;
    }
  };

  return { renderField };
}
